import { AppContext, AppContextValue } from "./AppContext";
import { useRouteItems } from "./common/infrastructure/routes/hooks/useRouteItems";
import { useSelectStudio } from "./common/infrastructure/hooks/useSelectStudio";
import { useMemo, useState } from "react";
import { useAuthentication } from "./common/infrastructure/hooks/useAuthentication";
import { IconType } from "./common/icons/Icon";
import { useDeviceType } from "./common/hooks/useDeviceType";
import { StudioType } from "./common/enums";

interface IAppContextProviderProps {
  children: React.ReactNode;
}

export const AppContextProvider = ({ children }: IAppContextProviderProps) => {
  const { isAuthenticated, currentUser, currentUserRole, logout } =
    useAuthentication();

  const [showSidebar, setShowSidebar] = useState(false);

  const [additionalLayoutButtonIconType, setAdditionalLayoutButtonIconType] =
    useState<IconType | undefined>();
  const [additionalLayoutButtonOnClick, setAdditionalLayoutButtonOnClick] =
    useState<(() => void) | undefined>(undefined);

  const deviceType = useDeviceType();

  const {
    userStudios,
    setSelectedStudioInStorage,
    requestStudioSwitch,
    pendingStudioAccess,
    confirmStudioAccess,
    confirmStudioAccessSilently,
    cancelStudioAccess,
    isRecordingStudioAccess,
    isStudioAccessCheckPending,
    selectedStudioId,
    resetStudios,
  } = useSelectStudio(currentUserRole, isAuthenticated);

  const { publicRoutes, privateRoutes } = useRouteItems(
    currentUser?.permissions
  );

  const selectedStudio = useMemo(
    () => userStudios?.find((x) => x.id === selectedStudioId),
    [selectedStudioId, isAuthenticated, userStudios]
  );

  const contextValue: AppContextValue = {
    publicRoutes,
    privateRoutes,
    additionalLayoutButtonIconType,
    userStudios,
    isAuthenticated,
    selectedStudioId,
    selectedStudio,
    currentUser,
    isDesktop: deviceType === "desktop",
    isDesktopOrTablet: deviceType === "desktop" || deviceType === "tablet",
    isTablet: deviceType === "tablet",
    isMobile: deviceType === "mobile",
    showGroupTypeOptions:
      selectedStudio?.types?.includes(StudioType.Group) ?? false,
    showServicesTypeOptions:
      selectedStudio?.types?.includes(StudioType.Service) ?? false,
    showGymTypeOptions:
      selectedStudio?.types?.includes(StudioType.Gym) ?? false,
    showStoreTypeOptions:
      selectedStudio?.types?.includes(StudioType.Store) ?? false,
    showSidebar,
    setShowSidebar,
    setSelectedStudioInStorage,
    requestStudioSwitch,
    pendingStudioAccess,
    confirmStudioAccess,
    confirmStudioAccessSilently,
    cancelStudioAccess,
    isRecordingStudioAccess,
    isStudioAccessCheckPending,
    resetStudios,
    logout,
    additionalLayoutButtonOnClick,
    setAdditionalLayoutButtonOnClick,
    setAdditionalLayoutButtonIconType,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};
